import { QueryClient } from '@tanstack/react-query';

/**
 * After a save, refresh EVERY cached copy of the data it touched — not only
 * the screens mounted right now.
 *
 * TanStack's default invalidation refetches active queries only; a list you
 * are not looking at is merely marked stale and keeps its old copy. Going back
 * to it (Save → detail → Contacts) then shows the pre-save values first and the
 * new ones only once a background refetch lands — on the live server that gap
 * is long enough to look like the save did not take. A targeted invalidation
 * (one with a queryKey or predicate) therefore refetches inactive copies too,
 * and a mutation that returns/awaits it finishes only once they are fresh.
 * An untargeted `invalidateQueries()` keeps the default (active only), so it
 * can never trigger a refetch of every cached query in the app.
 */
export function refreshInactiveOnInvalidate(client: QueryClient): QueryClient {
  const base = client.invalidateQueries.bind(client);
  client.invalidateQueries = ((filters?: Parameters<typeof base>[0], options?: Parameters<typeof base>[1]) => {
    const targeted = !!filters && (filters.queryKey !== undefined || filters.predicate !== undefined);
    const next = targeted && filters.refetchType === undefined ? { ...filters, refetchType: 'all' as const } : filters;
    return base(next, options);
  }) as typeof client.invalidateQueries;
  return client;
}

/**
 * Single source of server state. UI state lives in Zustand — never mirror
 * server data into a store.
 */
export const queryClient = refreshInactiveOnInvalidate(new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
}));
